/**
 * DecisionFlow — PlanConfigSummary
 * ui/src/components/tenant-admin/PlanConfigSummary.jsx
 *
 * Primary plan view for approved and pending_approval plans. Renders ABOVE
 * the modifier grid, which is demoted to secondary for non-draft states (AD-198).
 *
 * Spec authority: comp_policies_ui_spec_s736.md CHANGE-12
 *                 decisionflow_library_design_canon.md Part 1
 *
 * Shown when: isApproved || planStatus === 'pending_approval'
 * Not shown for: draft plans (grid remains primary — no change)
 *
 * Props:
 *   plan              {object}  — from GET /api/v1/plans/{id}
 *   components        {array}   — plan components array
 *   selectedComponent {object}  — currently selected component
 *   applicabilityRows {array}   — PCMA rows for selected component
 *   roleMap           {object}  — role_id → role object
 *   segmentMap        {object}  — segment_id → segment object
 *   buMap             {object}  — bu_id → bu object
 *   readiness         {object}  — from GET /plans/{id}/readiness (may be null)
 *   planAmendments    {array}   — amendment_log rows for this plan
 *
 * T2-B gap (L-128): modifier_summaries[] not yet in GET /plan-components/{id}.
 * Interim: show modifier_status label from applicabilityRows per AD-205.
 * summary_group supplied by local static map (AD-205) until backend ships.
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded colors.
 */

import { useState } from 'react';
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Clock, User } from 'lucide-react';

// ---------------------------------------------------------------------------
// AD-205 — Static summary_group map (interim until backend ships group metadata)
// Groups modifier types into the four accordion sections.
// ---------------------------------------------------------------------------
const MODIFIER_SUMMARY_GROUP = {
  'P3B-TXN':      'earning_structure',
  'P3B-REVTYPE':  'earning_structure',
  'P3B-SPLIT':    'payout_mechanics',
  'P3B-SPIFF':    'payout_mechanics',
  'P3B-KICKER':   'payout_mechanics',
  'P3B-CAP':      'payout_mechanics',
  'P3B-CLAWBACK': 'payout_mechanics',
  'P3B-DRAW':     'employment_mechanics',
  'P3B-OVERLAY':  'advanced',
};

const MODIFIER_DISPLAY_LABEL = {
  'P3B-TXN':      'Transaction Commission',
  'P3B-REVTYPE':  'Revenue Type Rules',
  'P3B-SPLIT':    'Deal Splits',
  'P3B-SPIFF':    'SPIFF / Tactical',
  'P3B-KICKER':   'Kicker / Multiplier',
  'P3B-CAP':      'Accelerator Cap',
  'P3B-CLAWBACK': 'Clawback Policy',
  'P3B-DRAW':     'Draw / Guarantee',
  'P3B-OVERLAY':  'Overlay Attribution',
};

// Status label for interim display (T2-B: no primary_summary yet)
const MODIFIER_STATUS_LABEL = {
  approved:         'Approved',
  pending_approval: 'Pending Approval',
  draft:            'Draft',
  rejected:         'Rejected',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtCurrency(val, code) {
  if (val == null) return '—';
  return `${code ?? ''}$${Number(val).toLocaleString()}`.trim();
}

function fmtPct(val) {
  if (val == null) return '—';
  return `${Math.round(Number(val) * 100)}%`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// AccordionSection — collapsible config section
// ---------------------------------------------------------------------------
function AccordionSection({ title, defaultOpen = true, children, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 bg-[rgb(var(--color-surface-muted))] text-left focus:outline-none"
      >
        <div className="flex items-center gap-2">
          <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{title}</span>
          {badge && (
            <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {badge}
            </span>
          )}
        </div>
        {open
          ? <ChevronDown  className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          : <ChevronRight className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      </button>
      {open && (
        <div className="divide-y divide-[rgb(var(--color-border))]">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModifierRow — single modifier in the config summary
// T2-B interim: shows status label until primary_summary is available from API.
// ---------------------------------------------------------------------------
function ModifierRow({ row }) {
  const label  = MODIFIER_DISPLAY_LABEL[row.modifier_type] ?? row.modifier_type;
  const status = row.modifier_status ?? 'not_started';
  const statusLabel = MODIFIER_STATUS_LABEL[status] ?? status;

  const statusColor =
    status === 'approved'         ? 'text-[rgb(var(--color-success))]' :
    status === 'pending_approval' ? 'text-[rgb(var(--color-info))]' :
    status === 'draft'            ? 'text-[rgb(var(--color-warning))]' :
    status === 'rejected'         ? 'text-[rgb(var(--color-danger))]' :
                                    'text-[rgb(var(--color-text-muted))]';

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-tok-sm text-[rgb(var(--color-text))]">{label}</span>
      <span className={`text-tok-xs font-medium ${statusColor}`}>{statusLabel}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanConfigSummary — main export
// ---------------------------------------------------------------------------
export default function PlanConfigSummary({
  plan,
  components = [],
  selectedComponent,
  applicabilityRows = [],
  roleMap = {},
  segmentMap = {},
  buMap = {},
  readiness = null,
  planAmendments = [],
}) {
  const [historyOpen, setHistoryOpen] = useState(false);

  if (!plan) return null;

  const planStatus   = plan.plan_status ?? 'draft';
  const isApproved   = planStatus === 'approved';
  const isPending    = planStatus === 'pending_approval';

  const roleName    = roleMap[plan.role_id]?.role_name    ?? roleMap[plan.role_id]?.role_code    ?? `Role ${plan.role_id}`;
  const segmentName = segmentMap[plan.segment_id]?.segment_name ?? segmentMap[plan.segment_id]?.segment_code ?? `Segment ${plan.segment_id}`;
  const buName      = plan.business_unit_id ? (buMap[plan.business_unit_id]?.bu_name ?? `BU ${plan.business_unit_id}`) : null;

  // OTE from plan (sourced from LATERAL JOIN on plan_component in GET /plans/{id})
  const oteMidpoint   = plan.ote_midpoint;
  const oteCurrency   = plan.ote_currency_code ?? '';
  const basePct       = selectedComponent?.base_pct;
  const variablePct   = selectedComponent?.variable_pct;

  // Component weight breakdown from selected component
  const arrWeight      = selectedComponent?.arr_weight;
  const mboWeight      = selectedComponent?.mbo_weight;
  const npsWeight      = selectedComponent?.nps_weight;
  const adoptionWeight = selectedComponent?.adoption_weight;

  // Readiness signal
  const readinessStatus  = readiness?.overall_status ?? null;
  const readinessChecks  = readiness?.checks ?? [];
  const blockerCount     = readinessChecks.filter((c) => c.status === 'fail').length;

  // Partition applicabilityRows by summary_group (AD-205 interim static map)
  const requiredRows  = applicabilityRows.filter((r) => r.applicability === 'required');
  const optionalRows  = applicabilityRows.filter((r) => r.applicability === 'optional');

  const earningRows     = requiredRows.filter((r) => MODIFIER_SUMMARY_GROUP[r.modifier_type] === 'earning_structure');
  const payoutRows      = requiredRows.filter((r) => MODIFIER_SUMMARY_GROUP[r.modifier_type] === 'payout_mechanics');
  const employmentRows  = requiredRows.filter((r) => MODIFIER_SUMMARY_GROUP[r.modifier_type] === 'employment_mechanics');
  const advancedRows    = requiredRows.filter((r) => MODIFIER_SUMMARY_GROUP[r.modifier_type] === 'advanced');

  // Optional modifiers not yet added (modifier_status null or not_started)
  const availableOptional = optionalRows.filter(
    (r) => !r.modifier_status || r.modifier_status === 'not_started'
  );

  // Amendment history
  const committedAmendments = planAmendments.filter((a) => a.amendment_status === 'committed');
  const openAmendment       = planAmendments.find((a) => a.amendment_status === 'open');

  return (
    <div className="space-y-4 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            {plan.plan_name ?? plan.plan_code}
          </h2>
          <div className="flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <span>{roleName}</span>
            <span className="text-[rgb(var(--color-border))]">·</span>
            <span>{segmentName}</span>
            {buName && (
              <>
                <span className="text-[rgb(var(--color-border))]">·</span>
                <span>{buName}</span>
              </>
            )}
            {plan.fiscal_period_id && (
              <>
                <span className="text-[rgb(var(--color-border))]">·</span>
                <span>Period {plan.fiscal_period_id}</span>
              </>
            )}
          </div>
        </div>

        {/* Readiness pill */}
        {readinessStatus && (
          <div className={[
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-tok-xs font-medium',
            readinessStatus === 'ready'
              ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
              : 'border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
          ].join(' ')}>
            {readinessStatus === 'ready'
              ? <CheckCircle2 className="h-3.5 w-3.5" />
              : <AlertTriangle className="h-3.5 w-3.5" />}
            {readinessStatus === 'ready'
              ? 'Ready for assignment'
              : `${blockerCount} blocker${blockerCount !== 1 ? 's' : ''}`}
          </div>
        )}
      </div>

      {/* ── Governance strip ── */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2.5">
        {/* Status badge */}
        <div className="flex items-center gap-1.5">
          <span className={[
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-tok-xs font-medium',
            isApproved
              ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
              : 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
          ].join(' ')}>
            {isApproved ? 'Approved' : 'Pending Approval'}
          </span>
        </div>
        {/* Approver */}
        {isApproved && plan.approved_by && (
          <div className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <User className="h-3.5 w-3.5" />
            <span>Approved by {String(plan.approved_by).slice(0, 8)}…</span>
          </div>
        )}
        {/* Approved at */}
        {isApproved && plan.approved_at && (
          <div className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            <Clock className="h-3.5 w-3.5" />
            <span>{fmtDate(plan.approved_at)}</span>
          </div>
        )}
        {/* Open amendment indicator */}
        {openAmendment && (
          <span className="flex items-center gap-1 text-tok-xs font-medium text-[rgb(var(--color-warning))]">
            <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] animate-pulse" />
            Amendment Open
          </span>
        )}
      </div>

      {/* ── Economic summary ── */}
      {(oteMidpoint != null || basePct != null) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-3">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">OTE Midpoint</p>
            <p className="mt-0.5 text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {fmtCurrency(oteMidpoint, oteCurrency)}
            </p>
          </div>
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-3">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Pay Mix</p>
            <p className="mt-0.5 text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {basePct != null && variablePct != null
                ? `${fmtPct(basePct)} / ${fmtPct(variablePct)}`
                : '—'}
            </p>
          </div>
          {arrWeight != null && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">ARR Weight</p>
              <p className="mt-0.5 text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmtPct(arrWeight)}</p>
            </div>
          )}
          {mboWeight != null && Number(mboWeight) > 0 && (
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-3">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">MBO Weight</p>
              <p className="mt-0.5 text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fmtPct(mboWeight)}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Active Configuration ── */}
      <div className="space-y-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Active Configuration
        </p>

        {/* Earning Structure */}
        {earningRows.length > 0 && (
          <AccordionSection title="Earning Structure" defaultOpen={true} badge={`${earningRows.length}`}>
            {earningRows.map((r) => <ModifierRow key={r.applicability_id ?? r.modifier_type} row={r} />)}
          </AccordionSection>
        )}

        {/* Payout Mechanics */}
        {payoutRows.length > 0 && (
          <AccordionSection title="Payout Mechanics" defaultOpen={true} badge={`${payoutRows.length}`}>
            {payoutRows.map((r) => <ModifierRow key={r.applicability_id ?? r.modifier_type} row={r} />)}
          </AccordionSection>
        )}

        {/* Employment Mechanics */}
        {employmentRows.length > 0 && (
          <AccordionSection title="Employment Mechanics" defaultOpen={true} badge={`${employmentRows.length}`}>
            {employmentRows.map((r) => <ModifierRow key={r.applicability_id ?? r.modifier_type} row={r} />)}
          </AccordionSection>
        )}

        {/* Advanced */}
        {advancedRows.length > 0 && (
          <AccordionSection title="Advanced" defaultOpen={false} badge={`${advancedRows.length}`}>
            {advancedRows.map((r) => <ModifierRow key={r.applicability_id ?? r.modifier_type} row={r} />)}
          </AccordionSection>
        )}

        {/* No active configuration */}
        {earningRows.length === 0 && payoutRows.length === 0 &&
         employmentRows.length === 0 && advancedRows.length === 0 && (
          <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-4 py-4 text-center">
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              No modifier configuration recorded for this component.
            </p>
          </div>
        )}
      </div>

      {/* ── Available (optional modifiers not yet added) ── */}
      {availableOptional.length > 0 && (
        <div className="space-y-2">
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
            Available to Add
          </p>
          <div className="rounded-xl border border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))]">
            {availableOptional.map((r) => (
              <div key={r.applicability_id ?? r.modifier_type} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                  {MODIFIER_DISPLAY_LABEL[r.modifier_type] ?? r.modifier_type}
                </span>
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic">Optional · not added</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Governance History (collapsed by default) ── */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 bg-[rgb(var(--color-surface-muted))] text-left focus:outline-none"
        >
          <div className="flex items-center gap-2">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Governance History</span>
            {committedAmendments.length > 0 && (
              <span className="inline-flex items-center rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {committedAmendments.length} amendment{committedAmendments.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {historyOpen
            ? <ChevronDown  className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            : <ChevronRight className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
        </button>
        {historyOpen && (
          <div className="divide-y divide-[rgb(var(--color-border))]">
            {committedAmendments.length === 0 ? (
              <div className="px-4 py-4 text-center">
                <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No amendments recorded.</p>
              </div>
            ) : committedAmendments.map((a) => (
              <div key={a.amendment_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">
                    A-{String(a.amendment_id).slice(0, 8)}
                  </span>
                  <span className="text-tok-xs text-[rgb(var(--color-text))]">
                    {a.amendment_reason_code?.replace(/_/g, ' ') ?? '—'}
                  </span>
                </div>
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {fmtDate(a.committed_at ?? a.updated_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
